// src/components/navigation/Sidebar.js

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSettings } from '../context/SettingsContext'; // ⭐ NEW: Import Settings Context
import { SunIcon, MoonIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { useChat } from '../../hooks/useChat';
import { useAbsenceNotifications } from '../../hooks/useAbsenceNotifications';
import { useStockNotifications } from '../../hooks/useStockNotifications'; // Add this line
import { useSupportNotifications } from '../../hooks/useSupportNotifications'; // ⭐ IMPORT THE NEW HOOK
import { useDeclinedJobsNotifications } from '../../hooks/useDeclinedJobsNotifications'; // ⭐ NEW
import { useDeclinedDailyClockNotifications } from '../../hooks/useDeclinedDailyClockNotifications'; // ⭐ NEW
import { useAssetsRenewalsNotifications } from '../../hooks/useAssetsRenewalsNotifications'; // ⭐ NEW

// --- Heroicons from @heroicons/react/24/outline ---
import {
    HomeIcon,
    UsersIcon,
    CalendarIcon,
    ChartPieIcon,
    Cog6ToothIcon,
    BriefcaseIcon,
    MapPinIcon,
    CurrencyDollarIcon,
    ChevronDoubleLeftIcon,
    ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';

// --- Lucide-React Icons ---
import {
    LayoutDashboard,
    CheckCircle,
    Megaphone,
    ReceiptText,
    LineChart,
    Package,
    PiggyBank,
    MessageSquare,
    ClipboardList,
    FilterIcon,
    CreditCard,
    Brush,
    Bell,
    HelpCircle,
    Mail,
    Bot, // <-- IMPORTED CHATBOT ICON
    Calculator,
    // ⭐ NEW IMPORT: GraduationCap for Training ⭐
    GraduationCap,
    // ⭐ NEW IMPORT: UserPlus for Recruitment / Onboarding ⭐
    UserPlus,
    // ⭐ NEW IMPORT: Tag for Discounts ⭐
    Tag,
    // ⭐ NEW IMPORT: Sparkles for Affiliate Program ⭐
    Sparkles,
    // ⭐ NEW: Icon for Smart Booking Form ⭐
    MousePointerClick,
    Smartphone,
    Trophy, // ⭐ NEW IMPORT: For Reward Lottery
    Truck, // ⭐ NEW IMPORT: For Fleet & Vehicles
    HeartPulse, // ⭐ NEW IMPORT: For Company Health Dashboard
    Store, // ⭐ NEW IMPORT: For Partner Directory
    FlaskConical, // ⭐ NEW IMPORT: For COSHH Sheets
    ClipboardCheck, // ⭐ NEW IMPORT: For Site Assessments
    Film, // ⭐ NEW IMPORT: For Marketing Reels
    ListChecks, // ⭐ NEW IMPORT: For Quote Forms
    LayoutTemplate, // ⭐ NEW IMPORT: For Landing Pages
    Coins // ⭐ NEW IMPORT: For Tips
} from 'lucide-react';

import Notifications from './Notifications';


const Sidebar = ({ isOpen, toggleSidebar }) => {
    const { user, logout } = useAuth();
    const { totalUnreadCount } = useChat();
    const { pendingCount } = useAbsenceNotifications();
    const { pendingStockCount } = useStockNotifications(); // Add this line
    const { unreadSupportCount } = useSupportNotifications(); // ⭐ GET THE UNREAD SUPPORT COUNT
    const { hasDeclinedJobs } = useDeclinedJobsNotifications(); // ⭐ NEW
    const { hasDeclinedDailyClock } = useDeclinedDailyClockNotifications(); // ⭐ NEW
    const { needsAttention: assetsRenewalsNeedAttention } = useAssetsRenewalsNotifications(); // ⭐ NEW
    const location = useLocation();
    const userRole = user?.role;
    const { t } = useTranslation();
    const { theme, toggleTheme } = useTheme();
    const { settings } = useSettings(); // ⭐ NEW: Grab settings context

    const classNames = (...classes) => {
        return classes.filter(Boolean).join(' ')
    }

    const isCurrent = (href) => {
        // ⭐ MODIFICATION: Added '/company-health', '/smart-booking', '/admin/lottery-settings', '/fleet', '/support', '/partner-directory', and '/recruitment' to check list
        if (['/dashboard', '/staff-dashboard', '/customer-portal', '/my-payslips', '/jobs', '/financial-dashboard', '/company-health', '/admin-chat', '/staff-chat', '/quotes', '/manager-job-card', '/landing-page-builder', '/recruitment', '/training-modules', '/discounts', '/affiliates/dashboard', '/smart-booking', '/admin/lottery-settings', '/fleet', '/support', '/partner-directory', '/coshh-sheets', '/site-assessments'].includes(href)) {
            return location.pathname.startsWith(href);
        }
        return location.pathname === href;
    };

    // ⭐ NEW: Get hidden tabs array from settings, default to empty array if undefined (Global Fallback)
    const hiddenManagerTabs = settings?.staffJobSettings?.hiddenNavTabsForManagers || [];
    // ⭐ NEW: Get hidden tabs array for admins, default to empty array if undefined
    const hiddenAdminTabs = settings?.staffJobSettings?.hiddenNavTabsForAdmins || [];

    // ⭐ NEW: Extract granular allowed tabs from the user's staff profile
    // Note: Depends on how your auth context nests the staff data. We check both user.staff and user directly.
    const granularManagerTabs = user?.staff?.accessibleTabs || user?.accessibleTabs || [];

    const navigation = [
        { name: t('sidebar.dashboard'), href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'manager'] },
        { name: t('sidebar.staffDashboard'), href: '/staff-dashboard', icon: HomeIcon, roles: ['staff'] },
        { name: t('sidebar.customerPortal'), href: '/customer-portal', icon: UsersIcon, roles: ['customer'] },
        { name: 'Switch to Staff View', href: '/manager-job-card', icon: ArrowRightOnRectangleIcon, roles: ['manager'] },
        { name: 'Switch to Staff View', href: '/manager-job-card', icon: ArrowRightOnRectangleIcon, roles: ['admin'], condition: (user) => !!user.staff },
        { name: t('sidebar.customers'), href: '/customers', icon: UsersIcon, roles: ['admin', 'manager'] },
        { name: t('sidebar.leads'), href: '/leads', icon: Megaphone, roles: ['admin', 'manager', 'staff'] },
        { name: t('sidebar.quotes'), href: '/quotes', icon: ClipboardList, roles: ['admin', 'manager'] },
        { name: 'Quote Calculator', href: '/quote-calculator', icon: Calculator, roles: ['admin', 'manager'] },
        
        // ⭐ NEW: Smart Booking Form Link ⭐
        { name: 'Smart Booking', href: '/smart-booking', icon: MousePointerClick, roles: ['admin', 'manager'] },

        // ⭐ NEW NAVIGATION ITEM: Discounts (Scratch Card Manager) ⭐
        { name: 'Discounts', href: '/discounts', icon: Tag, roles: ['admin', 'manager'] },

        // ⭐ NEW: Marketing Reels - branded before/after videos made from job photos ⭐
        { name: 'Marketing Reels', href: '/marketing-reels', icon: Film, roles: ['admin', 'manager'] },
        // ⭐ NEW: Landing Pages - converting pages made from a job's before/after photos ⭐
        { name: 'Landing Pages', href: '/landing-pages', icon: LayoutTemplate, roles: ['admin', 'manager'] },
        // ⭐ NEW: Quote Forms - ready-made stepped quote forms that create leads ⭐
        { name: 'Quote Forms', href: '/quote-forms', icon: ListChecks, roles: ['admin', 'manager'] },
        
        { name: t('sidebar.staff'), href: '/staff', icon: UsersIcon, roles: ['admin', 'manager'] },
        // ⭐ NEW: Company-wide Assets & Renewals page - reads/writes the same
        // records shown on the individual Staff/Customer profile modals.
        { name: 'Assets & Renewals', href: '/assets-renewals', icon: Package, roles: ['admin', 'manager'] },
        // ⭐ NEW: COSHH safety data sheets + QR codes
        { name: 'COSHH Sheets', href: '/coshh-sheets', icon: FlaskConical, roles: ['admin', 'manager'] },
        { name: t('sidebar.staffAbsence'), href: '/staff-absence', icon: BriefcaseIcon, roles: ['admin', 'manager'] },
        
        // ⭐ NEW: Recruitment Pipeline Link (Admin/Manager only) ⭐
        { name: 'Recruitment', href: '/recruitment', icon: UserPlus, roles: ['admin', 'manager'] },

        // ⭐ NEW: Training Modules Link (Admin/Manager only) ⭐
        { name: 'Training Modules', href: '/training-modules', icon: GraduationCap, roles: ['admin', 'manager'] },
        
        { name: t('sidebar.scheduler'), href: '/scheduler', icon: CalendarIcon, roles: ['admin', 'manager', 'staff'] },
        
        // ⭐ MODIFIED: Old Route Planner commented out as requested
        // { name: t('sidebar.routePlanner'), href: '/route-planner', icon: MapPinIcon, roles: ['admin', 'manager'] },
        
        { name: t('sidebar.spotChecker'), href: '/spot-checker', icon: CheckCircle, roles: ['admin', 'manager', 'staff'] },
        { name: t('sidebar.jobs'), href: '/jobs', icon: BriefcaseIcon, roles: ['admin', 'manager', 'staff'] },
        
        // ⭐ NEW: Fleet & Vehicles Link ⭐
        { name: 'Fleet & Vehicles', href: '/fleet', icon: Truck, roles: ['admin', 'manager'] },

        { name: t('sidebar.stock'), href: '/stock', icon: Package, roles: ['admin', 'manager', 'staff'] },
        { name: t('sidebar.invoices'), href: '/invoices', icon: ReceiptText, roles: ['admin', 'manager', 'staff'] },
        { name: 'Expenses', href: '/admin/expenses', icon: CreditCard, roles: ['admin', 'manager'] },
        { name: 'My Expenses', href: '/staff-portal/expenses', icon: CreditCard, roles: ['staff'] },
        // ⭐ MODIFIED: Changed "Payroll" to "Payments" ⭐
        { name: 'Payments', href: '/payroll', icon: CurrencyDollarIcon, roles: ['admin', 'manager'] },
        // ⭐ NEW: Customer tips - who they belong to and what's still to pay each staff member
        { name: 'Tips', href: '/tips', icon: Coins, roles: ['admin', 'manager'], requiresStripe: true }, // ⭐ tips are card only, so hidden until Stripe is connected
        // ⭐ MODIFIED: Changed "My Payslips" to "My Payments" to match page title ⭐
        { name: 'My Payments', href: '/my-payslips', icon: CurrencyDollarIcon, roles: ['staff'] },
        { name: t('sidebar.commissionReport'), href: '/commission-report', icon: LineChart, roles: ['admin', 'manager'] },
        { name: t('sidebar.financialDashboard'), href: '/financial-dashboard', icon: PiggyBank, roles: ['admin', 'manager'] },
        // ⭐ NEW: Company Health / Job Costing Engine ⭐
        { name: 'Company Health', href: '/company-health', icon: HeartPulse, roles: ['admin', 'manager'] },
        { name: t('sidebar.chat'), href: '/admin-chat', icon: MessageSquare, roles: ['admin', 'manager'] },
        { name: t('sidebar.chat'), href: '/staff-chat', icon: MessageSquare, roles: ['staff'] },
        { name: 'Message Templates', href: '/message-templates', icon: Mail, roles: ['admin'] },
        { name: 'Funnels', href: '/funnels', icon: FilterIcon, roles: ['admin', 'manager'] },
        /*{ name: 'Page Builder', href: '/landing-page-builder', icon: Brush, roles: ['admin', 'manager'] }, */
        { name: t('sidebar.formBuilder'), href: '/form-builder', icon: ChartPieIcon, roles: ['admin', 'manager'] },
        // ⭐ NEW: Site Assessments - fill in an assessment form on its own, then link it to a customer / site / job
        { name: 'Site Assessments', href: '/site-assessments', icon: ClipboardCheck, roles: ['admin', 'manager'] },
        /*{ name: 'Knowledge Base', href: '/knowledge-base', icon: HelpCircle, roles: ['admin', 'manager', 'staff'] }, */
        { name: 'Chatbot', href: '/chatbot-settings', icon: Bot, roles: ['admin', 'manager'] },
        
        // ⭐ NEW: Reward Lottery Link ⭐
        { name: 'Reward Lottery', href: '/admin/lottery-settings', icon: Trophy, roles: ['admin', 'manager'] },

        // ⭐ NEW: Affiliate Program Link (Above Settings) ⭐
        { name: 'Affiliate Program', href: '/affiliates/dashboard', icon: Sparkles, roles: ['admin', 'manager'] },
        // ⭐ NEW: Download App Link (Available to all roles) ⭐
        { name: 'Download App', href: '/download-app', icon: Smartphone, roles: ['admin', 'manager', 'staff', 'customer'] },

        // ⭐ NEW: Partner Directory Link ⭐
        { name: 'Partner Directory', href: '/partner-directory', icon: Store, roles: ['admin', 'manager'] },

        // ⭐ NEW: Help Centre / Support Link ⭐
        { name: 'Help Centre', href: '/support', icon: HelpCircle, roles: ['admin', 'manager'] },

        { name: t('sidebar.settings'), href: '/settings', icon: Cog6ToothIcon, roles: ['admin', 'manager'] },
    ].filter(item => {
        if (!item.roles.includes(userRole)) return false;
        if (item.condition && !item.condition(user)) return false;
        // ⭐ NEW: Stripe-only tabs (Tips) stay hidden until Stripe is connected in Settings, Payments
        if (item.requiresStripe && !settings?.stripeAccountId) return false;
        
        // ⭐ NEW: Granular Tab Permissions for Managers ⭐
        if (userRole === 'manager') {
            // FIX: Always allow managers to see the "Switch to Staff View" utility tab
            if (item.href === '/manager-job-card') {
                return true;
            }

            // 1. Check if the manager has individual specific tabs assigned (Whitelist check)
            if (granularManagerTabs.length > 0) {
                if (!granularManagerTabs.includes(item.href)) {
                    return false;
                }
            } 
            // 2. If no individual granular permissions exist, fallback to the global settings blacklist
            else if (hiddenManagerTabs.includes(item.href)) {
                return false;
            }
        }

        // ⭐ NEW: Hide tabs for admins if the admin restricted them for themselves ⭐
        // NOTE: '/settings' is exempt — it's the only place this toggle can be switched
        // back off, so hiding it would permanently lock admins out with no way back in.
        if (userRole === 'admin' && item.href !== '/settings' && hiddenAdminTabs.includes(item.href)) {
            return false;
        }

        return true;
    });


    return (
        <>
            {isOpen && (
                <div
                    className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[1040] md:hidden transition-opacity"
                    onClick={toggleSidebar}
                    aria-hidden="true"
                ></div>
            )}

            <div
                className={`fixed inset-y-0 left-0 z-[1050] flex h-full flex-col transition-all duration-300 ease-in-out
                            bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl
                            border-r border-slate-200/50 dark:border-slate-700/50
                            shadow-[8px_0_30px_rgb(0,0,0,0.05)] dark:shadow-[8px_0_30px_rgb(0,0,0,0.2)]
                            ${isOpen ? 'w-64 translate-x-0' : 'w-20 -translate-x-full md:translate-x-0'}
                            `}
                style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            >
                <div 
                    className="flex items-center justify-between px-5 border-b border-slate-200/50 dark:border-slate-700/50 shrink-0"
                    style={{ 
                        paddingTop: 'env(safe-area-inset-top, 0px)',
                        minHeight: 'calc(5rem + env(safe-area-inset-top, 0px))'
                    }}
                >
                     {isOpen && (
                        <span className="text-xl font-black bg-gradient-to-r from-indigo-600 to-cyan-500 dark:from-indigo-400 dark:to-cyan-300 bg-clip-text text-transparent tracking-tight truncate">
                            {t('appTitle')}
                        </span>
                     )}
                    <button
                        onClick={toggleSidebar}
                        className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors focus:outline-none md:hidden"
                        aria-label="Close sidebar"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <nav key={user?.uid} className="flex-1 px-3 py-5 space-y-1.5 overflow-y-auto custom-scrollbar">
                    {navigation.map((item) => {
                        const isChatLink = item.href === '/admin-chat' || item.href === '/staff-chat';
                        const isAbsenceLink = item.href === '/staff-absence';
                        const isStockLink = item.href === '/stock';
                        const isSupportLink = item.href === '/support'; // ⭐ FLAG FOR THE SUPPORT ROUTE
                        const isStaffLink = item.href === '/staff'; // ⭐ NEW: Correct link this time
                        const isAssetsRenewalsLink = item.href === '/assets-renewals'; // ⭐ NEW
                                        const active = isCurrent(item.href);

                        return (
                            <Link
                                key={item.name + item.href}
                                to={item.href}
                                className={classNames(
                                    active
                                        ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-md shadow-indigo-500/20 font-bold'
                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:hover:text-white hover:translate-x-1',
                                    'group flex items-center px-3 py-2.5 rounded-2xl relative transition-all duration-200',
                                    isOpen ? 'text-sm' : 'justify-center'
                                )}
                                aria-label={item.name}
                                onClick={() => { if (window.innerWidth < 768) toggleSidebar(); }}
                            >
                                <item.icon
                                    className={classNames(
                                        active ? 'text-white' : 'text-slate-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors',
                                        isOpen ? 'mr-3' : 'mr-0',
                                        'flex-shrink-0 h-[22px] w-[22px]'
                                    )}
                                    aria-hidden="true"
                                />
                                <span className={`${isOpen ? 'block truncate' : 'hidden'}`}>{item.name}</span>
                                
                                {isChatLink && totalUnreadCount > 0 && (
                                    <span className={`absolute w-2.5 h-2.5 bg-rose-500 rounded-full top-3 shadow-[0_0_8px_rgba(244,63,94,0.6)] ${isOpen ? 'right-3' : 'right-2'}`}></span>
                                )}
                                {/* 👇 ADD THIS BLOCK 👇 */}
                                {isAbsenceLink && pendingCount > 0 && (
                                    <span className={`absolute w-2.5 h-2.5 bg-amber-500 rounded-full top-3 shadow-[0_0_8px_rgba(245,158,11,0.6)] animate-pulse ${isOpen ? 'right-3' : 'right-2'}`}></span>
                                )}
                                {isStockLink && pendingStockCount > 0 && (
                                    <span className={`absolute w-2.5 h-2.5 bg-rose-500 rounded-full top-3 shadow-[0_0_8px_rgba(244,63,94,0.6)] ${isOpen ? 'right-3' : 'right-2'}`}></span>
                                )}
                                {/* ⭐ NEW: SUPPORT NOTIFICATION BADGE ⭐ */}
                                {isSupportLink && unreadSupportCount > 0 && (
                                    <span className={`absolute w-2.5 h-2.5 bg-rose-500 rounded-full top-3 shadow-[0_0_8px_rgba(244,63,94,0.6)] ${isOpen ? 'right-3' : 'right-2'}`}></span>
                                )}
                                {/* ⭐ NEW: DECLINED JOBS / DAILY CLOCK BADGE on the Staff nav item — gives the heads-up before they even drill into Time Clock or Daily Attendance */}
                                {isStaffLink && (hasDeclinedJobs || hasDeclinedDailyClock) && (
                                    <span className={`absolute w-2.5 h-2.5 bg-rose-500 rounded-full top-3 shadow-[0_0_8px_rgba(244,63,94,0.6)] animate-pulse ${isOpen ? 'right-3' : 'right-2'}`}></span>
                                )}
                                {/* ⭐ NEW: ASSETS & RENEWALS BADGE - overdue renewal or a Lost/Damaged asset */}
                                {isAssetsRenewalsLink && assetsRenewalsNeedAttention && (
                                    <span className={`absolute w-2.5 h-2.5 bg-rose-500 rounded-full top-3 shadow-[0_0_8px_rgba(244,63,94,0.6)] animate-pulse ${isOpen ? 'right-3' : 'right-2'}`}></span>
                                )}
                            </Link>
                        );
                    })}
                </nav>
                
                <div className="p-3 border-t border-slate-200/50 dark:border-slate-700/50 space-y-2 shrink-0">
                     {/* <Notifications isOpen={isOpen} /> */}
                     <button
                         onClick={toggleTheme}
                         className={`group flex items-center w-full px-3 py-2.5 rounded-2xl text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 ${!isOpen && 'justify-center'}`}
                         aria-label="Toggle theme"
                     >
                         {theme === 'light' ? (
                             <MoonIcon className={`flex-shrink-0 h-[22px] w-[22px] text-slate-400 group-hover:text-indigo-500 transition-colors ${isOpen ? 'mr-3' : 'mr-0'}`} />
                         ) : (
                             <SunIcon className={`flex-shrink-0 h-[22px] w-[22px] text-slate-400 group-hover:text-amber-400 transition-colors ${isOpen ? 'mr-3' : 'mr-0'}`} />
                         )}
                         <span className={`${isOpen ? 'block' : 'hidden'}`}>
                             {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                         </span>
                     </button>
                </div>


                <div className={`p-5 border-t border-slate-200/50 dark:border-slate-700/50 mt-auto shrink-0 transition-opacity duration-300 ${isOpen ? 'opacity-100 block' : 'opacity-0 hidden'}`}>
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                        <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold mb-1">{t('sidebar.loggedInAs')}</p>
                        <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{user?.contactPersonName || user?.email}</p>
                        <p className="text-xs text-slate-500 mt-0.5 capitalize">{t(`roles.${userRole}`)}</p>
                        
                        <button
                            onClick={logout}
                            className="mt-4 text-sm font-bold text-rose-500 hover:text-rose-600 flex items-center justify-center py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-500/10 dark:hover:bg-rose-500/20 transition-colors w-full"
                        >
                            <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                            {t('sidebar.logout')}
                        </button>
                    </div>
                </div>

                <div className="hidden md:flex items-center justify-center p-4 border-t border-slate-200/50 dark:border-slate-700/50 shrink-0">
                    <button
                        onClick={toggleSidebar}
                        className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 transition-all"
                        aria-label="Toggle sidebar"
                    >
                        <ChevronDoubleLeftIcon
                            className={`h-5 w-5 transition-transform duration-300 ease-in-out ${!isOpen && 'rotate-180'}`}
                        />
                    </button>
                </div>
            </div>
        </>
    );
};

export default Sidebar;